import React, {
  createRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import LinkWithStatus from '../common/LinkWithStatus'
import { getRequest, postRequest } from '../../services/base'
import { useParams } from 'react-router-dom'
import { NavContext } from '../../context/global_context'
import DownloadInlines from '../vehicle/inline_connections/DownloadInlines'
import { downloadFileBlob } from '../../utils/common'
import { useVehicles } from '../../context/vehicle_context'
import {
  Form as AntDForm,
  Flex,
  List,
  Modal,
  Row,
  Space,
  Typography,
} from 'antd'
import Button from '../common/Button'
import { CaretDownOutlined, CaretUpOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { UploadContext } from '../../context/upload_file_context'
import FileActions from './FileActions'
// import FileActions from '../../components/importers/FileActions'

export const ImporterRow = ({
  record,
  formRef,
  getImportFile,
  resetStations,
  downloadDTCMemo,
}) => {
  const { dependencies, file_name, file_type, is_uploading, key, versions } =
    record
  const hasDependencies = dependencies !== ''
  const hasUploadedVersions = versions.length > 0
  const isInlineCsv = file_name === 'inlines.csv'
  const isDtcMemosCsv = file_name === 'dtc_memos.csv'

  const filenameClass = hasDependencies ? 'not-available' : 'available'
  const dependenciesClass = hasDependencies
    ? 'not-available'
    : hasUploadedVersions && !is_uploading
      ? 'available'
      : ''
  const dependenciesContent = hasDependencies
    ? dependencies
    : hasUploadedVersions && !is_uploading
      ? 'Available'
      : ''

  return (
    <Flex align="flex-start" gap={16} className={`importer-grid row ${hasDependencies || dependenciesContent == '' ? 'has-dependencies' : ''}`}>
      <div className={`file-column ${filenameClass}`}>{file_name}</div>

      <div className={`dependency-column ${dependenciesContent == '' ? 'hide' : ''}`}>
        <span className="status-wrapper">
          <span className={`status-icon ${dependenciesClass}`}></span>
          <span className={`status-text ${dependenciesClass}`}>
            {dependenciesContent}
          </span>
        </span>
      </div>

      <div className={`actions-column ${hasDependencies ? 'hide' : ''}`}>
        {
          isDtcMemosCsv ? (
            <Button
              type="link"
              onClick={() => downloadDTCMemo()}
              className="link-button"
            >
              Download
            </Button>
          ) : (
            <>
              {
                isInlineCsv ? (
                  <DownloadInlines isButton={false} />
                ) : null
              }
              <FileActions
                fileId={file_name}
                filesList={versions}
                fileType={key}
                fileName={file_name}
                formRef={formRef}
                is_uploading={is_uploading}
                reloadData={() => {
                  getImportFile()
                }}
                resetStations={() => {
                  resetStations()
                }}
              />
            </>
          )
        }
      </div>
    </Flex>
  )
}

const FilesForm = ({}) => {
  const interval = useRef(null)
  const formRef = createRef()
  const [value, dispatch] = useContext(NavContext)
  const [importFiles, setImportFiles] = useState([])
  const { selectedVehicleName, setHasBlockers } = useVehicles()
  const [form] = AntDForm.useForm()
  const [sortOrder, setSortOrder] = useState(null)
  const [uploadState] = useContext(UploadContext)
  const [modal, contextHolder] = Modal.useModal()

  let { vehicle } = useParams()

  useEffect(() => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Fetching Importers...',
        showLoader: true,
      },
      notification: {},
    })
    getImportFile()

    return () => {
      closeInterval()
    }
  }, [])

  const closeInterval = () => {
    if (interval.current) {
      clearInterval(interval.current)
      interval.current = null
      setHasBlockers(false)
    }
  }

  const callFunctionInterval = () => {
    setHasBlockers(true)
    interval.current = setInterval(() => {
      getImportFile()
    }, 15000)
  }

  const getImportFile = async () => {
    let res = await getRequest(
      `/api/importer/import_types/?vehicle_id=${vehicle}`
    )

    setImportFiles([
      {
        dependencies: '',
        file_name: 'dtc_memos.csv',
        file_type: null,
        is_uploading: false,
        key: 'dtc_memos',
        versions: []
      },
      ...res.data.data
    ])

    dispatch({
      type: 'header',
      data: {
        header: 'Admin Tools',
        sub_header: `Vehicle List / ${selectedVehicleName} / Importers`,
        id: res.data.vehicle_data.id,
        show_back_btn: true,
        make_model_id: res.data.vehicle_data.make_model_id,
      },
    })

    if (res.data.is_upload_going_on) {
      if (!interval.current) {
        callFunctionInterval()
      }
    } else {
      closeInterval()
    }

    dispatch({
      type: 'loader',
      data: {
        caption: '',
        showLoader: false,
      },
      notification: {},
    })
  }

  const downloadDTCMemo = async () => {
    let res = await getRequest(`/api/dtc_memos/export/${vehicle}/`, true, true)
    if (res?.msg) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: res?.msg,
        },
      })
    } else {
      downloadFileBlob(res, `${vehicle}_dtc_memo.csv`)
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: 'File Download completed.',
        },
      })
    }
  }

  const showResetStationsConfirm = () => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Reset Imports of Stations',
      width: '480px',
      content: (
        <>
          Are you sure you want to reset all imports of stations ?
        </>
      ),
      centered: true,
      maskClosable: true,
      okText: 'Reset',
      cancelText: 'Cancel',
      onOk: resetStations,
    })
  }

  const resetStations = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Station reset is in progress..',
        showLoader: true,
      },
      notification: {},
    })

    const res = await postRequest(`/api/stations/reset/${vehicle}/`)
    if (res?.status) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: 'Station Successfully deleted!',
        },
      })
      getImportFile()
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: res?.msg,
        },
      })
    }
  }

  // Sort Importer data
  const toggleSort = () => {
    setSortOrder((prev) => {
      switch (prev) {
        case null:
          return 'asc'
        case 'asc':
          return 'desc'
        default:
          return null
      }
    })
  }

  const sortedData = useMemo(() => {
    if (!sortOrder) return importFiles
  
    return [...importFiles].sort((a, b) => {
      return sortOrder === 'asc'
        ? a.dependencies.localeCompare(b.dependencies)
        : b.dependencies.localeCompare(a.dependencies)
    })
  }, [importFiles, sortOrder])

  // Reset other Select components on selection change
  useEffect(() => {
    if (!uploadState?.file_type) return
    
    // Antd throws a circular refernce warning if select component is updated individually, hence usign a single update 
    const resetFields = {}

    importFiles.forEach((file) => {
      if (file.key !== uploadState.file_type) {
        resetFields[file.key] = undefined
      }
    })

    form.setFieldsValue(resetFields)
  }, [uploadState, form])

  return (
    <>
      <div className="page edit-vehicle">
        <Row className="title">
          <Space size={'middle'}>
            <Typography.Title level={5}>Importer / Downloads</Typography.Title>
          </Space>
        </Row>
        <Row className="body">
          <AntDForm
            id="vehicle-form-importers"
            name="vehicle-form-importers"
            form={form}
            layout="vertical"
          >
            <List
              bordered
              dataSource={sortedData}
              className="importers-list"
              header={
                <div className="importer-grid">
                  <div>Name</div>

                  <Flex
                    align="center"
                    gap={8}
                    className='dependency-column-header'
                    onClick={toggleSort}
                  >
                    <span>Current Status</span>
                    <div className="sort-icon">
                      <CaretUpOutlined className={sortOrder === 'asc' ? 'active' : ''}/>
                      <CaretDownOutlined className={sortOrder === 'desc' ? 'active' : ''} />
                    </div>
                  </Flex>
                  
                  <div></div>
                </div>
              }
              renderItem={(record) => (
                <List.Item>
                  <ImporterRow
                    record={record}
                    formRef={form}
                    getImportFile={getImportFile}
                    resetStations={showResetStationsConfirm}
                    downloadDTCMemo={downloadDTCMemo}
                  />
                </List.Item>
              )}
            />
          </AntDForm>
        </Row>
      </div>

      {contextHolder}
    </>
  )
}

FilesForm.propTypes = {}

export default FilesForm
